import React, { useState, useRef, useEffect, useMemo } from "react";
import Map, { Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { client } from "../../services/api";
import { toast } from "react-toastify";
import wkt from "wellknown";
import bbox from "@turf/bbox";
import ModalAddStrucure from "../ModalAddStrucure";
import { getLabelByValue, structureTypes } from "../../constants/formOptions";

function StepObjective({ formData, updateFormData }) {
  const [isAddStrucureModalOpen, setIsAddStrucureModalOpen] = useState(false);
  const [isAddAssessStrucureModalOpen, setIsAddAssessStrucureModalOpen] = useState(false);
  const mapRef = useRef();
  const [viewport, setViewport] = useState({
    latitude: 41.8719,
    longitude: 12.56,
    zoom: 10,
  });

  const toggleCheckbox = (value) => {
    updateFormData("selectedObjectives", formData.selectedObjectives.includes(value)
      ? formData.selectedObjectives.filter((item) => item !== value)
      : [...formData.selectedObjectives, value]);
  };

  const addStructure = (structure) => {
    updateFormData("structures", [...formData.structures, structure]);
  };

  const removeStructure = async (structure) => {
    if (structure.dataFile && window.confirm("The structure file uploaded will be deleted, continue?")) {
      await client.deleteGeometry(formData.project._id, structure.dataFile._id)
        .then(() => toast.success("Structure file deleted successfully!"));
    }
    updateFormData("structures", formData.structures.filter((item) => item._id !== structure._id));
  };

  const structureBounds = useMemo(() => {
    const structures = formData?.structures || [];

    const extentGeoms = structures
      .map(s => s.dataFile?.layer?.extent)
      .filter(Boolean)
      .map(extentWKT => wkt.parse(extentWKT));

    if (extentGeoms.length === 0) return null;

    const featureCollection = {
      type: "FeatureCollection",
      features: extentGeoms.map(geom => ({
        type: "Feature",
        geometry: geom,
      })),
    };

    return bbox(featureCollection);
  }, [formData?.structures]);

  useEffect(() => {
    if (mapRef.current && structureBounds) {
      mapRef.current.fitBounds(structureBounds, { padding: 20 });
    }
  }, [structureBounds]);

  return (
    <div className="objective-wrapper">
      <h6>Main Objective</h6>
      <div className="row-flex">
        <div>
          <div className="area-container" style={{ display: "block", maxWidth: 'none', marginBottom: '12px' }}>
            <label>
              <input
                type="checkbox"
                name="mainObjective"
                value="evaluate"
                checked={formData.selectedObjectives.includes("evaluate")}
                onChange={() => toggleCheckbox("evaluate")}
              />
              An existing infrastructure
            </label>
            {formData.selectedObjectives.includes("evaluate") && (
              <>
                <ul className="item-list">
                  {formData.structures.filter(item => item.objective === "evaluate").map((item, index) => (
                    <li key={index} style={{ backgroundColor: '#F2F3F7', border: 'none' }}>
                      {item.name}{" "}
                      <span>{getLabelByValue(structureTypes, item.type)}</span>
                      <i className='icon-remove' style={{ float: 'right' }} onClick={() => removeStructure(item)}></i>
                    </li>
                  ))}
                </ul>
                <button style={{ margin: '15px' }} className="action-link" onClick={() => setIsAddStrucureModalOpen(true)}>+ Add an infrastructure</button>
                <ModalAddStrucure projectId={formData.project._id} isOpen={isAddStrucureModalOpen} onClose={() => setIsAddStrucureModalOpen(false)} onDone={addStructure} objective="evaluate" />
              </>
            )}
          </div>
          <div className="area-container" style={{ display: "block", maxWidth: 'none', marginBottom: '12px' }}>
            <label>
              <input
                type="checkbox"
                name="mainObjective"
                value="assess"
                checked={formData.selectedObjectives.includes("assess")}
                onChange={() => toggleCheckbox("assess")}
              />
              The environmental impact on an existing infrastructure
            </label>
            {formData.selectedObjectives.includes("assess") && (
              <>
                <ul className="item-list">
                  {formData.structures.filter(item => item.objective === "assess").map((item, index) => (
                    <li key={index} style={{ backgroundColor: '#F2F3F7', border: 'none' }}>
                      {item.name}{" "}
                      <span>{getLabelByValue(structureTypes, item.type)}</span>
                      <i className='icon-remove' style={{ float: 'right' }} onClick={() => removeStructure(item)}></i>
                    </li>
                  ))}
                </ul>
                <button style={{ margin: '15px' }} className="action-link" onClick={() => setIsAddAssessStrucureModalOpen(true)}>+ Add an infrastructure</button>
                <ModalAddStrucure projectId={formData.project._id} isOpen={isAddAssessStrucureModalOpen} onClose={() => setIsAddAssessStrucureModalOpen(false)} onDone={addStructure} objective="assess" />
              </>
            )}
          </div>
          <div className="area-container" style={{ maxWidth: 'none' }}>
            <label>
              <input
                type="checkbox"
                name="mainObjective"
                value="context"
                checked={formData.selectedObjectives.includes("context")}
                onChange={() => toggleCheckbox("context")}
              />
              The environment (geohazard assessement)
            </label>
          </div>
        </div>
        <div className="map-wrapper">
          <Map
            reuseMaps
            ref={mapRef}
            className="area-map"
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={viewport}
            mapStyle="mapbox://styles/mapbox/streets-v11"
            onMove={(evt) => setViewport(evt.viewState)}
          >
            {formData?.structures.map(s => {
              if (!s.dataFile?._id) return null;
              const layerId = `layer-${s.type}-${s.dataFile._id}`;
              return <Source key={s.dataFile?._id} type="vector" url={`mapbox://${s.dataFile?.layer?.source_id}`}>
                <Layer {...{
                  id: layerId,
                  'source-layer': s.dataFile?.layer?.source_layer,
                  type: 'fill',
                  paint: {
                    'fill-color': '#010E2D',
                    'fill-opacity': 0.5,
                  },
                }} />
              </Source>;
            })}
          </Map>
        </div>
      </div>
    </div>
  );
}

export default StepObjective;
